import React, { useState, lazy, Suspense } from 'react';
import { checkinTicket } from '../apiEndpoints';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, DrawerContent, DrawerHeader, DrawerBody } from '@heroui/drawer';
import useIsMobile from '../hooks/useIsMobile';

interface GlobalQRScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
}

const BarcodeScanner = lazy(() => import("react-qr-barcode-scanner"));

export default function GlobalQRScannerModal({ isOpen, onClose, eventId }: GlobalQRScannerModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [scannedData, setScannedData] = useState('');
    const queryClient = useQueryClient();

    const handleQrScan = async (result: any) => {
        if (result && result.text && !isLoading) {
            setIsLoading(true);
            setError('');
            setSuccess('');
            setScannedData(result.text);

            try {
                // Parse the QR code data to extract order ID and ticket QR
                const qrData = result.text;
                const [orderId, ticketQr] = qrData.split(':');

                if (!orderId || !ticketQr) {
                    throw new Error('Invalid QR code format. Expected format: orderId:ticketQr');
                }

                await checkinTicket(orderId, ticketQr, 'qr');
                setSuccess('Ticket checked in successfully!');

                // Refresh the orders data
                queryClient.invalidateQueries({ queryKey: ['eventOrders', eventId] });

                // Close modal after 2 seconds
                setTimeout(() => {
                    onClose();
                    setSuccess('');
                    setScannedData('');
                }, 2000);
            } catch (err: any) {
                setError(err.response?.data?.message || err.message || 'Failed to check in ticket');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleQrError = (err: any) => {
        console.error('QR Scanner error:', err);
        // Only show critical errors, ignore scanning errors
        if (err && (err.includes('NotFound') || err.includes('NotAllowed') || err.includes('NotReadable') || err.includes('Overconstrained') || err.includes('NotSupported') || err.includes('Permission'))) {
            setError('Camera access error. Please check your camera permissions and try again.');
        }
    };

    const isMobile = useIsMobile();

    return (
        <Drawer
            placement="right"
            hideCloseButton
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'md'}
            scrollBehavior="inside"
            classNames={{
                wrapper: isMobile ? 'z-[100000]' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000]',
                base: isMobile ? 'bg-white dark:bg-nightSurface h-[100dvh] my-0 z-[100000]' : 'bg-white dark:bg-nightSurface h-[calc(100dvh-80px)] my-0 z-[100000] rounded-l-[28px] ring-1 ring-black/[0.06] dark:ring-white/10',
                backdrop: isMobile ? 'z-[100000] bg-black/50' : 'top-[80px] h-[calc(100dvh-80px)] z-[100000] bg-black/50',
            }}
        >
            <DrawerContent>
                <DrawerHeader className="flex items-start justify-between px-6 pt-6 pb-4">
                    <div>
                        <h2 className="text-[28px] font-bold leading-[1] tracking-[-0.04em] text-grey10 dark:text-white">Scan QR</h2>
                        <p className="text-sm font-normal text-grey40 dark:text-nightMuted mt-2">Scan any ticket QR code to check in attendees</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 text-grey10 dark:text-white hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors flex-shrink-0"
                        aria-label="Close"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </DrawerHeader>

                {/* Content */}
                <DrawerBody className="px-6 py-2">
                    <div className="space-y-4">
                        <div className="relative">
                            <div className="w-full h-fit rounded-[28px] overflow-hidden bg-[#0a0a0a] ring-1 ring-black/[0.06] dark:ring-white/10">
                                <Suspense fallback={<div className="w-full h-96 flex items-center justify-center text-sm font-semibold text-white/60">Loading scanner...</div>}>
                                    <BarcodeScanner
                                        width={400}
                                        height={400}
                                        onUpdate={(err, result) => {
                                            if (err) {
                                                handleQrError(err);
                                            } else if (result) {
                                                handleQrScan(result);
                                            }
                                        }}
                                    />
                                </Suspense>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="w-48 h-48 border-2 border-white/30 dark:border-white/20 rounded-[28px] relative">
                                    <div className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 rounded-tl-[28px] border-primary40 dark:border-nightAccent"></div>
                                    <div className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 rounded-tr-[28px] border-primary40 dark:border-nightAccent"></div>
                                    <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 rounded-bl-[28px] border-primary40 dark:border-nightAccent"></div>
                                    <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 rounded-br-[28px] border-primary40 dark:border-nightAccent"></div>
                                </div>
                            </div>
                        </div>

                        {isLoading && (
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary40 dark:border-nightAccent"></div>
                                <p className="text-sm font-semibold text-grey40 dark:text-nightMuted mt-2">Processing...</p>
                            </div>
                        )}

                        {scannedData && (
                            <div className="p-4 rounded-2xl bg-grey99 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted mb-1">Scanned Data:</p>
                                <p className="text-sm font-mono text-grey10 dark:text-white break-all">{scannedData}</p>
                            </div>
                        )}
                    </div>

                    {/* Error/Success Messages */}
                    {error && (
                        <div className="mt-4 rounded-full bg-error95 px-4 py-2.5 text-center dark:bg-error50/15">
                            <p className="text-sm font-semibold text-error40 dark:text-error70">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="mt-4 rounded-full bg-success95 px-4 py-2.5 text-center dark:bg-success50/15">
                            <p className="text-sm font-semibold text-success30 dark:text-success60">{success}</p>
                        </div>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}